import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'

import { useUser } from '../../../hooks'
import { Lead, LeadTourStatus } from '../../../models/Outreach/types'
import { useLeadTour } from '../../../hooks/Outreach/useLeadTour'

const TOUR_SLOT_START_MINUTES = 8 * 60
const TOUR_SLOT_END_MINUTES = 20 * 60

function pad2(value: number): string {
    return String(value).padStart(2, '0')
}

function minutesToValue(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return `${pad2(hours)}:${pad2(minutes)}`
}

function formatSlotLabel(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    const period = hours >= 12 ? 'PM' : 'AM'
    const hour12 = hours % 12 === 0 ? 12 : hours % 12
    return `${hour12}:${pad2(minutes)} ${period}`
}

const TOUR_TIME_SLOTS = (() => {
    const slots: { value: string; label: string }[] = []
    for (
        let minutes = TOUR_SLOT_START_MINUTES;
        minutes <= TOUR_SLOT_END_MINUTES;
        minutes += 15
    ) {
        slots.push({
            value: minutesToValue(minutes),
            label: formatSlotLabel(minutes),
        })
    }
    return slots
})()

function snapToTourSlot(date: Date): string {
    const raw = date.getHours() * 60 + date.getMinutes()
    const snapped = Math.round(raw / 15) * 15
    const clamped = Math.min(
        TOUR_SLOT_END_MINUTES,
        Math.max(TOUR_SLOT_START_MINUTES, snapped),
    )
    return minutesToValue(clamped)
}

function defaultTourDateTime(): { date: Date; time: string } {
    const now = new Date()
    const current = now.getHours() * 60 + now.getMinutes()
    let next = Math.ceil(current / 15) * 15
    if (next === current) {
        next += 15
    }
    const date = new Date(now)
    if (next > TOUR_SLOT_END_MINUTES) {
        date.setDate(date.getDate() + 1)
        return { date, time: '10:00' }
    }
    if (next < TOUR_SLOT_START_MINUTES) {
        return { date, time: '10:00' }
    }
    return { date, time: minutesToValue(next) }
}

function combineTourDateTime(date: Date | null, time: string): string | null {
    if (!date || Number.isNaN(date.getTime()) || !time) {
        return null
    }
    const [hours, minutes] = time.split(':').map(Number)
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
        return null
    }
    const next = new Date(date)
    next.setHours(hours, minutes, 0, 0)
    return next.toISOString()
}

interface ScheduleTourDialogProps {
    open: boolean
    lead: Lead | null
    /** When set, dialog edits this tour instead of creating. */
    tourId?: number | null
    initialStatus?: LeadTourStatus
    onClose: () => void
    onSuccess?: (lead: Lead) => void
}

export const ScheduleTourDialog: React.FC<ScheduleTourDialogProps> = ({
    open,
    lead,
    tourId = null,
    initialStatus = 'scheduled',
    onClose,
    onSuccess,
}) => {
    const { workspaceUser, userList } = useUser({ shouldAuthenticate: false })
    const {
        createLeadTour,
        updateLeadTour,
        isCreatingLeadTour,
        isUpdatingLeadTour,
    } = useLeadTour()

    const existingTour = useMemo(() => {
        if (!lead || tourId == null) {
            return null
        }
        return (
            lead.tours?.find((t) => t.id === tourId) ??
            (lead.current_tour?.id === tourId ? lead.current_tour : null)
        )
    }, [lead, tourId])

    const [tourDate, setTourDate] = useState<Date | null>(null)
    const [tourTime, setTourTime] = useState('')
    const [givenById, setGivenById] = useState<number | ''>('')
    const [notes, setNotes] = useState('')
    const [status, setStatus] = useState<LeadTourStatus>('scheduled')

    const staffOptions = useMemo(() => {
        const byId = new Map<number, { id: number; name: string }>()
        for (const u of userList ?? []) {
            if (u?.id != null) {
                byId.set(u.id, {
                    id: u.id,
                    name: u.name || u.email || `User ${u.id}`,
                })
            }
        }
        if (workspaceUser?.id != null && !byId.has(workspaceUser.id)) {
            byId.set(workspaceUser.id, {
                id: workspaceUser.id,
                name:
                    workspaceUser.name ||
                    workspaceUser.email ||
                    `User ${workspaceUser.id}`,
            })
        }
        if (existingTour?.given_by && !byId.has(existingTour.given_by.id)) {
            byId.set(existingTour.given_by.id, existingTour.given_by)
        }
        return Array.from(byId.values()).sort((a, b) =>
            a.name.localeCompare(b.name),
        )
    }, [userList, workspaceUser, existingTour])

    useEffect(() => {
        if (!open || !lead) {
            return
        }
        if (existingTour) {
            const existingStart = existingTour.starts_at
                ? new Date(existingTour.starts_at)
                : null
            if (existingStart && !Number.isNaN(existingStart.getTime())) {
                setTourDate(existingStart)
                setTourTime(snapToTourSlot(existingStart))
            } else {
                const defaults = defaultTourDateTime()
                setTourDate(defaults.date)
                setTourTime(defaults.time)
            }
            setGivenById(existingTour.given_by?.id ?? '')
            setNotes(existingTour.notes ?? '')
            setStatus(existingTour.status)
        } else {
            const defaults = defaultTourDateTime()
            setTourDate(defaults.date)
            setTourTime(defaults.time)
            setGivenById(workspaceUser?.id ?? '')
            setNotes('')
            setStatus(initialStatus)
        }
    }, [open, lead, existingTour, workspaceUser?.id, initialStatus])

    if (!lead) {
        return null
    }

    const loading = isCreatingLeadTour || isUpdatingLeadTour
    const isEdit = existingTour != null

    const handleSubmit = async () => {
        const startsIso =
            status === 'skipped'
                ? null
                : combineTourDateTime(tourDate, tourTime)
        if (status !== 'skipped' && !startsIso) {
            return
        }

        const payload = {
            status,
            starts_at: startsIso,
            given_by_id: givenById === '' ? null : Number(givenById),
            notes: notes.trim(),
        }

        const result = isEdit
            ? await updateLeadTour({
                  leadId: lead.id,
                  tourId: existingTour!.id,
                  payload,
              })
            : await createLeadTour({
                  leadId: lead.id,
                  payload,
              })

        onSuccess?.(result.lead)
        onClose()
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Edit tour' : 'Schedule tour'}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" paragraph>
                    {statusHelp(status, isEdit)}
                </Typography>
                <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel id="tour-status-label">Status</InputLabel>
                    <Select
                        labelId="tour-status-label"
                        label="Status"
                        value={status}
                        onChange={(event) =>
                            setStatus(event.target.value as LeadTourStatus)
                        }
                    >
                        <MenuItem value="scheduled">Scheduled</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="no_show">No show</MenuItem>
                        <MenuItem value="skipped">Skipped</MenuItem>
                    </Select>
                </FormControl>
                {status !== 'skipped' && (
                    <Box display="flex" style={{ gap: 12 }}>
                        <KeyboardDatePicker
                            label="Date"
                            format="MM/dd/yyyy"
                            inputVariant="outlined"
                            variant="inline"
                            autoOk
                            disableToolbar
                            margin="normal"
                            value={tourDate}
                            onChange={(date) => setTourDate(date)}
                            style={{ flex: 1 }}
                        />
                        <FormControl
                            variant="outlined"
                            margin="normal"
                            style={{ flex: 1 }}
                        >
                            <InputLabel id="tour-time-label">Time</InputLabel>
                            <Select
                                labelId="tour-time-label"
                                label="Time"
                                value={tourTime}
                                onChange={(event) =>
                                    setTourTime(String(event.target.value))
                                }
                            >
                                {TOUR_TIME_SLOTS.map((slot) => (
                                    <MenuItem
                                        key={slot.value}
                                        value={slot.value}
                                    >
                                        {slot.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}
                <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel id="tour-given-by-label">Given by</InputLabel>
                    <Select
                        labelId="tour-given-by-label"
                        label="Given by"
                        value={givenById}
                        onChange={(e) =>
                            setGivenById(
                                e.target.value === ''
                                    ? ''
                                    : Number(e.target.value),
                            )
                        }
                    >
                        <MenuItem value="">
                            <em>Unassigned</em>
                        </MenuItem>
                        {staffOptions.map((u) => (
                            <MenuItem key={u.id} value={u.id}>
                                {u.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    fullWidth
                    variant="outlined"
                    label={status === 'completed' ? 'Tour notes' : 'Notes'}
                    helperText={
                        status === 'completed'
                            ? 'How did the tour go?'
                            : undefined
                    }
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    margin="normal"
                    multiline
                    rows={3}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={
                        loading ||
                        (status !== 'skipped' &&
                            !combineTourDateTime(tourDate, tourTime))
                    }
                >
                    {loading ? 'Saving…' : saveLabel(status, isEdit)}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function statusHelp(status: LeadTourStatus, isEdit: boolean): string {
    if (status === 'skipped') {
        return 'No visit is on the calendar.'
    }
    if (status === 'completed') {
        return 'They toured. Add a note about how it went.'
    }
    if (status === 'no_show') {
        return 'They had a tour booked and did not come.'
    }
    return isEdit
        ? 'Update the tour time, staff, or outcome.'
        : 'Book a property tour. You can change who gave it later.'
}

function saveLabel(status: LeadTourStatus, isEdit: boolean): string {
    if (isEdit) {
        return 'Save tour'
    }
    if (status === 'completed') {
        return 'Mark completed'
    }
    if (status === 'no_show') {
        return 'Mark no-show'
    }
    if (status === 'skipped') {
        return 'Mark skipped'
    }
    return 'Schedule'
}
